import React, { useEffect, useState } from "react";
import * as yup from "yup";
import axios from "lib/api";
import Api from "lib/api";
import { ButtonRow, SeparateLine } from "styles/elements/main";
import { AccordionComponent, Button, GenericError } from "components";
import { deleteSingleAttachment } from "modules/shared/api";
import changedForm from "hooks/useChangedForm";
import EditHandler from "modules/drivers/components/EditHandler";
import { validationSchema } from "../validationSchema";
import { mapSupplierDataToApi } from "../config/mapUiToData";
import { mapSupplierByIdToUi } from "../config/mapOptionsToUi";
import trackAnalytics from "trackAnalytics";
// eslint-disable-next-line import/no-cycle
import { PersonalInfo, WorkingInfo, CompanyInfo, PaymentInfo } from ".";
import Spinner from "components/Spinner";
import useFetchSupplierData from "../config/useFetchSupplierData";
import RejectionList from "components/rejectionList";
import { useSelector } from "react-redux";
import { authorizedActionsSelector } from "modules/auth/slice";
import checkAuthorization from "modules/auth/helpers/checkAuthorization";

function EditSupplier({ selectedSupplierId, onClose, onSuccess }) {
    const [supplierData, setSupplierData] = useState(null);
    const [errorList, setErrorList] = useState(null);
    const [errors, setErrors] = useState(null);
    const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false);
    const [isEditingWorkinglInfo, setIsEditingWorkingInfo] = useState(false);
    const [isEditingCompanyInfo, setIsEditingCompnayInfo] = useState(false);
    const [isEditingPaymentInfo, setIsEditingPaymentInfo] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [phoneNumberError, setPhoneNumberError] = useState(null);
    const [nationalIdError, setNationalIdError] = useState(null);
    const [eWalletNumberError, setEWalletNumberError] = useState(null);
    const authorizedActions = useSelector(authorizedActionsSelector());
    const isAuthorizedToCreate = checkAuthorization(authorizedActions, "drivers", "create");
    const { selectedSupplierData, supplierAttachments, setSupplierAttachments, objections } =
        useFetchSupplierData(
            selectedSupplierId,
            "paymentMethod,attachments,objections,objections.section,objections.item"
        );
    const handleSupplierDetailsChange = (e, name) => {
        if (!name) {
            const { name, value } = e?.target;
            setSupplierData({
                ...supplierData,
                [name]: value,
            });
            yup.reach(validationSchema, name)
                .validate(value)
                .then(() => setErrorList({ ...errorList, [name]: "" }))
                .catch((err) => setErrorList({ ...errorList, [name]: err.message }));
        } else {
            setSupplierData({
                ...supplierData,
                [name]: e,
            });

            yup.reach(validationSchema, name)
                .validate(e)
                .then(() => setErrorList({ ...errorList, [name]: "" }))
                .catch((err) => setErrorList({ ...errorList, [name]: err.message }));
        }
    };
    const handleDeleteCompanyAttachments = async () => {
        const filteredAttachments = supplierAttachments?.filter(
            (attachment) =>
                attachment?.documentType !== "tax_card" &&
                attachment?.documentType !== "commercial_register"
        );
        try {
            await Promise.all(
                supplierAttachments
                    .filter(
                        (attachment) =>
                            attachment?.documentType === "tax_card" ||
                            attachment?.documentType === "commercial_register"
                    )
                    .map(async (attachment) => {
                        await deleteSingleAttachment(attachment?.id);
                    })
            );
            setSupplierAttachments(filteredAttachments);
        } catch (error) {
            return error;
        }
    };
    const handleEditSupplier = async (e) => {
        e.preventDefault();
        try {
            let newData = { ...supplierData };
            if (supplierData?.hasACompany?.value === false) {
                handleDeleteCompanyAttachments();
            }
            if (!supplierData.paymentMethod) {
                newData = { ...supplierData, paymentMethodDelete: true };
            } else {
                newData = { ...supplierData, paymentMethodDelete: false };
            }
            setIsLoading(true);
            const changedData = changedForm(selectedSupplierData, supplierData);
            trackAnalytics("supplier_profile_submit_edit", changedData);
            const response = await Api.put(
                `suppliers/${selectedSupplierId}`,
                await mapSupplierDataToApi(newData, selectedSupplierId)
            );
            setIsLoading(false);
            onSuccess(supplierData.arabicName);
        } catch (error) {
            setIsLoading(false);

            setErrors(error?.errors);
            if (error?.errors?.phone_number) {
                setPhoneNumberError(error?.errors?.phone_number?.[0]);
            }
            if (error?.errors?.national_id) {
                setNationalIdError(error?.errors?.national_id?.[0]);
            }
            if (error?.errors?.["payment_method.e_wallet_number"]) {
                setEWalletNumberError(error?.errors?.["payment_method.e_wallet_number"]?.[0]);
            }
            return error;
        }
    };

    useEffect(() => {
        setSupplierData(selectedSupplierData);
    }, [selectedSupplierData]);

    useEffect(() => {
        setPhoneNumberError(null);
    }, [supplierData?.phoneNumber]);

    useEffect(() => {
        setNationalIdError(null);
    }, [supplierData?.nationalId]);

    useEffect(() => {
        setEWalletNumberError(null);
    }, [supplierData?.eWalletNumber]);

    return (
        <>
            {Object.keys(objections).length > 0 && isAuthorizedToCreate && (
                <RejectionList objections={objections} />
            )}
            <AccordionComponent
                title="Personal Info"
                defaultExpanded
                extraAction={
                    <EditHandler
                        isOnEdit={isEditingPersonalInfo}
                        setIsOnEdit={setIsEditingPersonalInfo}
                    />
                }
                body={
                    <PersonalInfo
                        newSupplierData={supplierData}
                        setNewSupplierData={setSupplierData}
                        handleSupplierDetailsChange={handleSupplierDetailsChange}
                        isEditable={isEditingPersonalInfo}
                        supplierAttachments={supplierAttachments}
                        setSupplierAttachments={setSupplierAttachments}
                        selectedSupplierId={selectedSupplierId}
                        errorList={errorList}
                        phoneNumberError={phoneNumberError}
                        nationalIdError={nationalIdError}
                        objections={objections["Personal Info - Supplier"] ?? null}
                        isAuthorizedToCreate={isAuthorizedToCreate}
                    />
                }
            />
            <SeparateLine />
            <AccordionComponent
                title="Working Info"
                defaultExpanded
                extraAction={
                    <EditHandler
                        isOnEdit={isEditingWorkinglInfo}
                        setIsOnEdit={setIsEditingWorkingInfo}
                    />
                }
                body={
                    <WorkingInfo
                        newSupplierData={supplierData}
                        setNewSupplierData={setSupplierData}
                        handleSupplierDetailsChange={handleSupplierDetailsChange}
                        isEditable={isEditingWorkinglInfo}
                        errorList={errorList}
                        objections={objections["Working Info - Supplier"] ?? null}
                        isAuthorizedToCreate={isAuthorizedToCreate}
                    />
                }
            />
            <SeparateLine />
            <AccordionComponent
                title="Company Info"
                defaultExpanded
                extraAction={
                    <EditHandler
                        isOnEdit={isEditingCompanyInfo}
                        setIsOnEdit={setIsEditingCompnayInfo}
                    />
                }
                body={
                    <CompanyInfo
                        newSupplierData={supplierData}
                        setNewSupplierData={setSupplierData}
                        handleSupplierDetailsChange={handleSupplierDetailsChange}
                        isEditable={isEditingCompanyInfo}
                        supplierAttachments={supplierAttachments}
                        setSupplierAttachments={setSupplierAttachments}
                        selectedSupplierId={selectedSupplierId}
                        errorList={errorList}
                        eWalletNumberError={eWalletNumberError}
                        objections={objections["Company Info - Supplier"] ?? null}
                        isAuthorizedToCreate={isAuthorizedToCreate}
                    />
                }
            />
            <SeparateLine />
            <AccordionComponent
                title="Payment Info"
                defaultExpanded
                extraAction={
                    <EditHandler
                        isOnEdit={isEditingPaymentInfo}
                        setIsOnEdit={setIsEditingPaymentInfo}
                    />
                }
                body={
                    <PaymentInfo
                        newSupplierData={supplierData}
                        setNewSupplierData={setSupplierData}
                        handleSupplierDetailsChange={handleSupplierDetailsChange}
                        isEditable={isEditingPaymentInfo}
                        errorList={errorList}
                        objections={objections["Payments Info - Supplier"] ?? null}
                        isAuthorizedToCreate={isAuthorizedToCreate}
                    />
                }
            />
            {errors && <GenericError errorMassege={errors} />}
            <ButtonRow>
                <Button
                    type="button"
                    onChange={onClose}
                    name="Cancel"
                    btnStyle="secondBtn"
                />
                <Button
                    type="submit"
                    onChange={handleEditSupplier}
                    name={isLoading ? <Spinner /> : "Submit"}
                    btnStyle="primaryBtn"
                />
            </ButtonRow>
        </>
    );
}

export default EditSupplier;
